import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import SEOHead from '@/components/SEOHead';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from '@/components/ui/accordion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  Search, 
  Clock, 
  Users, 
  Shield, 
  Trophy, 
  Calendar as CalendarIcon,
  MessageCircle,
  Star,
  Gift,
  Settings,
  Smartphone,
  Zap,
  UserCheck,
  HelpCircle,
  Award,
  Target,
  Grid3X3,
  ExternalLink,
  ArrowRight,
  CheckCircle2,
  PlayCircle,
  BookOpen,
  FileText,
  User,
  UserPlus,
  Gamepad2,
  Mail,
  Phone,
  HeadphonesIcon,
  Copy,
  ThumbsUp,
  ThumbsDown
} from 'lucide-react';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { usePlayerRegistrations } from '@/hooks/usePlayerRegistrations';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useIsMobile } from '@/hooks/useIsMobile';

const FAQ = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string | null>(null);
  const [helpfulVotes, setHelpfulVotes] = useState<{[key: string]: 'up' | 'down' | null}>({});
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [openAccordionItem, setOpenAccordionItem] = useState<string | undefined>(undefined);
  const isMobile = useIsMobile();
  const { user: internalUser } = useInternalAuth();
  const isAdmin = internalUser?.role === 'admin';
  const navigate = useNavigate();
  const categoryRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const { openRegistrationModal, PlayerRegistrationModalComponent } = usePlayerRegistrations();

  // Quick action navigation items
  const quickActions = [
    { label: 'Create Account', icon: <UserPlus className="h-4 w-4" />, action: () => navigate('/register'), color: 'bg-blue-500' },
    { label: 'Dashboard', icon: <Grid3X3 className="h-4 w-4" />, action: () => navigate('/dashboard'), color: 'bg-green-500' },
    { label: 'Join Team', icon: <Users className="h-4 w-4" />, action: () => navigate('/teams'), color: 'bg-purple-500' },
    { label: 'Contact Support', icon: <HeadphonesIcon className="h-4 w-4" />, action: () => navigate('/support'), color: 'bg-orange-500' },
  ];

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(type);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleHelpfulVote = (questionId: string, vote: 'up' | 'down') => {
    setHelpfulVotes(prev => ({
      ...prev,
      [questionId]: prev[questionId] === vote ? null : vote
    }));
  };

  const faqCategories = [
    {
      category: 'Getting Started',
      icon: <Star className="h-5 w-5" />,
      description: 'New to SportManager? Start here for account setup and first steps.',
      quickStats: '4 essential steps',
      questions: [
        {
          id: 'getting-started-1',
          question: 'How do I create an account?',
          answer: 'Click the "Sign Up" button and fill out the registration form. You\'ll need to provide basic information and select your role (player, coach, team owner, etc.). After registration, complete your profile verification to access all features.',
          actionButtons: [
            { label: 'Sign Up Now', action: () => navigate('/register'), variant: 'default' as const, icon: <UserPlus className="h-4 w-4" /> },
            { label: 'View Roles Guide', action: () => {
              setActiveCategory('Getting Started');
              setOpenAccordionItem('Getting Started-1');
              setTimeout(() => {
                document.getElementById('faq-getting-started-1')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 100);
            }, variant: 'outline' as const, icon: <BookOpen className="h-4 w-4" /> }
          ]
        },
        {
          id: 'getting-started-2',
          question: 'What are the different user roles?',
          answer: 'We support 7 primary roles for account creation: Player, Coach, Team Owner, Venue Manager, ScoreKeeper, Fan, and Parent. Each role has different permissions and capabilities within the platform.',
          actionButtons: [
            { label: 'Complete Profile', action: () => navigate('/profile'), variant: 'default' as const, icon: <User className="h-4 w-4" /> },
            { label: 'Role Comparison', action: () => navigate('/faq'), variant: 'outline' as const, icon: <FileText className="h-4 w-4" />, adminOnly: true }
          ],
          roleCards: [
            { role: 'Player', description: 'Join teams and participate in games', permissions: ['Join teams', 'View schedules', 'Track stats'] },
            { role: 'Coach', description: 'Manage teams and create games', permissions: ['Create games', 'Manage rosters', 'Team communication'] },
            { role: 'Team Owner', description: 'Full team control and management', permissions: ['All coach permissions', 'Team deletion', 'Role changes'] },
            { role: 'Venue Manager', description: 'List and manage venue bookings', permissions: ['Manage venues', 'Create games', 'Handle bookings'] },
            { role: 'ScoreKeeper', description: 'Official game scoring and statistics', permissions: ['Score games', 'Earn points', 'View assignments'] },
            { role: 'Fan', description: 'Follow teams and engage with content', permissions: ['Follow teams', 'Get notifications', 'Engage with content'] },
            { role: 'Parent', description: 'Manage child accounts and activities', permissions: ['Manage child accounts', 'Approve activities', 'View schedules'] }
          ]
        },
        {
          id: 'getting-started-3',
          question: 'How do I complete my profile verification?',
          answer: 'After creating your account, navigate to your profile settings and complete all required fields including contact information, emergency contacts, and role-specific details. Verification helps ensure safety and authenticity on our platform.',
          actionButtons: [
            { label: 'Complete Verification', action: () => navigate('/profile/complete'), variant: 'default' as const, icon: <CheckCircle2 className="h-4 w-4" /> }
          ],
          steps: [
            { step: 1, title: 'Basic Information', description: 'Complete name, contact details, and emergency contacts' },
            { step: 2, title: 'Role Verification', description: 'Provide role-specific information and documentation' },
            { step: 3, title: 'Safety Check', description: 'Review privacy settings and safety guidelines' },
            { step: 4, title: 'Account Activation', description: 'Your account will be verified and activated' }
          ]
        },
        {
          id: 'getting-started-4',
          question: 'What should I do first after signing up?',
          answer: 'Complete your profile verification, explore the dashboard to familiarize yourself with features, join or create teams if applicable, and set up your notification preferences to stay informed about activities.',
          actionButtons: [
            { label: 'Go to Dashboard', action: () => navigate('/dashboard'), variant: 'default' as const, icon: <Grid3X3 className="h-4 w-4" /> },
            { label: 'Watch Tutorial', action: () => {}, variant: 'outline' as const, icon: <PlayCircle className="h-4 w-4" /> }
          ],
          checklist: [
            'Complete profile verification',
            'Explore dashboard features',
            'Join or create teams',
            'Set notification preferences',
            'Download mobile app'
          ]
        }
      ]
    },
    {
      category: 'Points & Rewards',
      icon: <Trophy className="h-5 w-5" />,
      description: 'Learn about earning, tracking, and managing your points.',
      quickStats: '1-year cycle',
      questions: [
        {
          id: 'points-reset',
          question: 'Do my points reset? When?',
          answer: 'Yes! Your points operate on a 1-year cycle from your signup date or last reset. After 1 year, your points will automatically reset to zero, and you\'ll start fresh. However, your point history is preserved and can be viewed at any time. Admins can also export your full point activity log if needed for disputes or records.',
          highlightBox: {
            type: 'warning',
            content: '⚠️ Points automatically reset after 1 year. Check your Points page to see how many days remain in your current cycle.'
          },
          actionButtons: [
            { 
              label: 'View My Points', 
              action: () => navigate('/points'), 
              variant: 'default' as const, 
              icon: <Trophy className="h-4 w-4" /> 
            }
          ]
        },
        {
          id: 'points-earning',
          question: 'How can I earn points?',
          answer: 'You can earn points through various activities: Daily login (10 pts), Completing profile (50 pts), Welcome bonus (25 pts), Referrals (100 pts for referrer, 50 pts for new user), Participating in games, and Completing tasks.',
          actionButtons: [
            { 
              label: 'Check My Activity', 
              action: () => navigate('/points'), 
              variant: 'default' as const 
            }
          ],
          checklist: [
            'Daily Login: 10 points',
            'Complete Profile: 50 points',
            'Welcome Bonus: 25 points',
            'Refer a Friend: 100 points (you) + 50 points (friend)',
            'Game Participation: Variable points',
            'Task Completion: Variable points'
          ]
        },
        {
          id: 'points-dispute',
          question: 'What if I disagree with my points total?',
          answer: 'If you believe there\'s an error with your points, contact support with details. Admins can review your complete point activity log (all transactions with dates and descriptions) and resolve any discrepancies. Your full history is maintained even after yearly resets.',
          actionButtons: [
            { 
              label: 'Contact Support', 
              action: () => navigate('/support'), 
              variant: 'default' as const,
              icon: <HeadphonesIcon className="h-4 w-4" />
            }
          ]
        },
        {
          id: 'points-history',
          question: 'Can I see my past years\' points?',
          answer: 'Yes! While your current active points reset yearly, your complete point history is preserved. Visit your Points page to view your transaction history. Admins can also export your full activity log as a CSV file for record-keeping.',
          actionButtons: [
            { 
              label: 'View History', 
              action: () => navigate('/points'), 
              variant: 'default' as const 
            }
          ]
        }
      ]
    },
    {
      category: 'Game Rules & Management',
      icon: <Gamepad2 className="h-5 w-5" />,
      description: 'Understanding game creation, confirmation rules, and management.',
      quickStats: '6 key rules',
      questions: [
        {
          id: 'game-rules-create',
          question: 'How Do I Create Games?',
          answer: 'Only Coaches, Team Owners, and Venue Managers can create games. Once created, you\'ll need to fill out the game form with all required details and confirm team participation within the required timeframes.',
          desktopInstructions: {
            title: 'Desktop Navigation:',
            steps: [
              '1. Click on your profile menu (top right corner with your avatar/name)',
              '2. Look for "Game Time" in the dropdown menu',
              '3. Click "Create Game" from the submenu',
              '4. Fill out the game form with all required information',
              '5. Submit and share the game code with the opposing team'
            ]
          },
          mobileInstructions: {
            title: 'Mobile Navigation:',
            steps: [
              '1. Tap the menu icon (three horizontal lines) in the top right',
              '2. Tap on your profile name or avatar',
              '3. Find and tap "Game Time" in the menu',
              '4. Tap "Create Game" from the options',
              '5. Complete the game form on your mobile device',
              '6. Submit and share the game code'
            ]
          },
          eligibleRoles: [
            { 
              role: 'Coach', 
              description: 'Can create games and select their own team as Home or Away',
              icon: '👨‍🏫'
            },
            { 
              role: 'Team Owner', 
              description: 'Full game creation access for their teams',
              icon: '🏆'
            },
            { 
              role: 'Venue Manager', 
              description: 'Can create games at their venue and select any participating teams',
              icon: '🏟️'
            }
          ],
          importantNotes: [
            'Players, Fans, Parents, and Scorekeepers cannot create games',
            'You can only add your own team (Coaches/Team Owners)',
            'Venue Managers can select any teams for games at their venue',
            'Opposing teams must confirm within 24 hours of game start',
            'Make sure to complete all required sections of the game form'
          ],
          actionButtons: [
            { 
              label: 'Create Game Now', 
              action: () => navigate('/create-game'), 
              variant: 'default' as const, 
              icon: <Gamepad2 className="h-4 w-4" /> 
            },
            { 
              label: 'View My Game Forms', 
              action: () => navigate('/my-game-forms'), 
              variant: 'outline' as const, 
              icon: <FileText className="h-4 w-4" /> 
            }
          ],
          flowSteps: [
            { step: 1, title: 'Access Menu', description: 'Navigate to your profile menu' },
            { step: 2, title: 'Find Game Time', description: 'Select "Game Time" option' },
            { step: 3, title: 'Create Game', description: 'Click "Create Game"' },
            { step: 4, title: 'Fill Form', description: 'Complete all required fields' },
            { step: 5, title: 'Submit', description: 'Share game code with opponent' }
          ]
        },
        {
          id: 'game-rules-1',
          question: 'What is the 24-hour confirmation rule?',
          answer: 'When a coach or team owner creates a game and selects their team as either "Home" or "Away", the opposing team must confirm their participation within 24 hours of the game start time. If they fail to confirm, the game will be automatically cancelled with the reason "Defending Team did not confirm".',
          actionButtons: [
            { label: 'Create Game', action: () => navigate('/create-game'), variant: 'default' as const, icon: <Gamepad2 className="h-4 w-4" /> },
            { label: 'View Game Rules', action: () => navigate('/rules'), variant: 'outline' as const, icon: <FileText className="h-4 w-4" /> }
          ],
          timeline: [
            { time: 'Game Creation', description: 'Coach creates game and selects home/away team' },
            { time: '24 Hours Before', description: 'Opposing team must confirm participation' },
            { time: 'Confirmation Deadline', description: 'Auto-cancellation if no confirmation' },
            { time: 'Game Time', description: 'Confirmed games proceed as scheduled' }
          ]
        },
        {
          id: 'game-rules-2',
          question: 'Why can\'t I add other teams when creating a game?',
          answer: 'Coaches and team owners can only add their own teams to prevent forfeit situations and negative impacts on other teams. You can only select your own team as either the "Home" or "Away" team. The opposing team must confirm the game themselves to ensure it\'s a legitimate matchup.',
          actionButtons: [
            { label: 'Create Game', action: () => navigate('/create-game'), variant: 'default' as const, icon: <Gamepad2 className="h-4 w-4" /> },
            { label: 'Team Management', action: () => navigate('/teams'), variant: 'outline' as const, icon: <Users className="h-4 w-4" /> }
          ]
        },
        {
          id: 'game-rules-3',
          question: 'What happens if I use a Game Code to join a game?',
          answer: 'The same 24-hour confirmation rule applies when using Game Codes. If you add your team to an existing game using a code, you still must confirm your participation within 24 hours, or the game will be cancelled.',
          actionButtons: [
            { label: 'Browse Games', action: () => navigate('/games'), variant: 'default' as const, icon: <Grid3X3 className="h-4 w-4" /> }
          ],
          codeExample: 'GAME-2024-ABC123'
        },
        {
          id: 'game-rules-4',
          question: 'What are the player roster requirements?',
          answer: 'Both teams must add all participating players and confirm their status by game time. Teams must also confirm player availability before the game starts. Failure to meet minimum roster requirements or confirm player status may result in forfeit.',
          actionButtons: [
            { label: 'My Teams', action: () => navigate('/teams'), variant: 'default' as const, icon: <Users className="h-4 w-4" /> }
          ]
        },
        {
          id: 'game-rules-5',
          question: 'How do venue-created games work?',
          answer: 'When a venue creates a game, they can select any teams to participate. However, both selected teams must confirm their participation within 24 hours of the game start time. Teams must also complete their rosters and confirm player status by game time.',
          actionButtons: [
            { label: 'Find Venues', action: () => navigate('/venues'), variant: 'default' as const, icon: <CalendarIcon className="h-4 w-4" /> }
          ]
        },
        {
          id: 'game-rules-6',
          question: 'How does game workflow automation work?',
          answer: 'Our platform automatically manages game workflows including reminders, confirmations, roster checks, and status updates. You\'ll receive notifications at key milestones to ensure games run smoothly.',
          actionButtons: []
        },
        {
          id: 'game-rules-7',
          question: 'What does "Present at Venue" status mean?',
          answer: 'Present at Venue means the player is physically at the venue but has not yet confirmed they are ready to play. Players in this status who are not updated to Ready/Confirmed by game start will automatically be listed as "No Show".',
          actionButtons: [],
          statusExplanation: [
            { status: 'Present at Venue', description: 'Player is at location but not ready yet' },
            { status: 'Ready/Confirmed', description: 'Player confirmed and ready to play' },
            { status: 'No Show', description: 'Automatically assigned if not updated by game start' }
          ]
        }
      ]
    },
    {
      category: 'Scorekeeper System',
      icon: <UserCheck className="h-5 w-5" />,
      description: 'Become a scorekeeper and earn points for your contributions.',
      quickStats: 'Earn up to 100 points per game',
      questions: [
        {
          id: 'scorekeeper-1',
          question: 'How do I apply to become a scorekeeper?',
          answer: 'Navigate to the Scorekeeper Applications section in your dashboard. Complete the application form including your experience, availability, and references. Applications are reviewed by venue administrators and coaches. Note: You must be signed in to apply.',
          actionButtons: [
            { label: 'Apply Now (Sign In Required)', action: () => navigate('/scorekeeper/applications'), variant: 'default' as const, icon: <UserPlus className="h-4 w-4" /> }
          ],
          requirements: [
            'Basic knowledge of the sport',
            'Availability for games',
            'Reliable internet connection',
            'Professional references'
          ]
        },
        {
          id: 'scorekeeper-2',
          question: 'How does scorekeeper assignment work?',
          answer: 'Scorekeepers are assigned to games based on availability, proximity to venue, experience level, and previous performance ratings. You\'ll receive notifications when assigned to games.',
          actionButtons: [
            { label: 'View Assignments', action: () => navigate('/scorekeeper/assignments'), variant: 'default' as const, icon: <CalendarIcon className="h-4 w-4" /> }
          ]
        },
        {
          id: 'scorekeeper-3',
          question: 'What points do I earn for scorekeeping?',
          answer: 'Scorekeepers earn points for each game they complete successfully. Points vary based on game importance, complexity, and performance rating. Bonus points are awarded for exceptional service and reliability.',
          actionButtons: [],
          pointsTable: [
            { gameType: 'Regular Game', points: '50-75 points', bonus: 'Reliability bonus +10' },
            { gameType: 'Tournament Game', points: '75-100 points', bonus: 'Excellence bonus +15' },
            { gameType: 'Championship', points: '100-150 points', bonus: 'Special event +25' }
          ]
        },
        {
          id: 'scorekeeper-4',
          question: 'What are my responsibilities as a scorekeeper?',
          answer: 'Accurately track game statistics, maintain fair and unbiased scoring, arrive on time, communicate with teams and officials, and submit complete game reports promptly after each game.',
          actionButtons: [],
          responsibilities: [
            'Accurate game statistics tracking',
            'Fair and unbiased scoring',
            'Punctual arrival and setup',
            'Professional communication',
            'Complete game report submission'
          ]
        },
        {
          id: 'scorekeeper-5',
          question: 'Can I decline scorekeeper assignments?',
          answer: 'Yes, you can decline assignments, but frequent declines may affect your future assignment priority. It\'s best to update your availability regularly to receive appropriate assignments.',
          actionButtons: [
            { label: 'Manage Assignments', action: () => navigate('/scorekeeper/assignments'), variant: 'outline' as const, icon: <Settings className="h-4 w-4" /> }
          ]
        }
      ]
    },
    {
      category: 'Points & Rewards System',
      icon: <Gift className="h-5 w-5" />,
      questions: [
        {
          question: 'How do I earn points on the platform?',
          answer: 'Points are earned through various activities: playing games, scorekeeping, completing profile information, referring new users, participating in events, and achieving milestones. Each activity has different point values.'
        },
        {
          question: 'What are daily bonuses and how do they work?',
          answer: 'Daily bonuses are awarded for logging in and completing daily activities like checking your dashboard, updating availability, or engaging with team communications. Consecutive days increase bonus multipliers.'
        },
        {
          question: 'How do leaderboards work?',
          answer: 'Leaderboards track user performance across different categories like points earned, games played, scorekeeping accuracy, and team contributions. Rankings are updated in real-time and reset periodically.'
        },
        {
          question: 'Can I redeem points for rewards?',
          answer: 'Points can be redeemed for various rewards including platform features, merchandise, venue discounts, and special recognitions. Check the rewards section for current offerings and point requirements.'
        },
        {
          question: 'Do points expire?',
          answer: 'Points remain active as long as you maintain regular platform activity. Inactive accounts may see point decay after extended periods of non-use. Regular participation keeps all points active.'
        }
      ]
    },
    {
      category: 'Team Management',
      icon: <Users className="h-5 w-5" />,
      description: 'Create, join, and manage teams with ease.',
      quickStats: 'Unlimited team participation',
      questions: [
        {
          id: 'team-1',
          question: 'How do I create a team?',
          answer: 'Team creation is available to Coaches and Team Owners. Navigate to the Teams section, click "Create Team," fill out team details including name, sport, age group, and skill level. Add team code for easy player invitations.',
          actionButtons: [
            { label: 'My Teams', action: () => navigate('/teams'), variant: 'default' as const, icon: <Users className="h-4 w-4" /> }
          ],
          steps: [
            { step: 1, title: 'Basic Information', description: 'Team name, sport, and location' },
            { step: 2, title: 'Team Settings', description: 'Age group, skill level, and privacy' },
            { step: 3, title: 'Generate Code', description: 'Create unique team code for invitations' },
            { step: 4, title: 'Invite Players', description: 'Share code or send direct invitations' }
          ]
        },
        {
          id: 'team-2',
          question: 'How do I join a team?',
          answer: 'You can join a team by using a team code provided by your coach or team owner, accepting a direct invitation, or requesting to join through the team\'s public profile if they allow open applications.',
          actionButtons: [
            { label: 'Find Teams', action: () => navigate('/user-discovery'), variant: 'default' as const, icon: <Search className="h-4 w-4" /> }
          ],
          joinMethods: [
            { method: 'Team Code', description: 'Enter 6-digit code from your coach', icon: <Grid3X3 className="h-4 w-4" /> },
            { method: 'Direct Invitation', description: 'Accept email or app invitation', icon: <Mail className="h-4 w-4" /> },
            { method: 'Public Teams', description: 'Browse and request to join', icon: <Search className="h-4 w-4" /> }
          ]
        },
        {
          id: 'team-3',
          question: 'How do team codes work?',
          answer: 'Team codes are unique identifiers that allow easy team joining. Coaches and Team Owners can generate and share these codes. Players enter the code in their dashboard to send a join request to the team.',
          actionButtons: [],
          codeExample: 'TEAM-ABC123',
          codeFeatures: [
            'Unique 6-digit identifier',
            'Expires after 30 days',
            'Can be regenerated anytime',
            'Tracks who joins with code'
          ]
        },
        {
          id: 'team-4',
          question: 'What are the different team roles and permissions?',
          answer: 'Team Owner: Full control including team deletion and role changes. Coach: Game creation, roster management, player invitations. Captain: Limited roster management and communication. Player: Participation in games and team activities.',
          actionButtons: [],
          rolesTable: [
            { role: 'Team Owner', permissions: ['Full control', 'Delete team', 'Change roles', 'Financial management'] },
            { role: 'Coach', permissions: ['Create games', 'Manage roster', 'Team communication', 'Player invitations'] },
            { role: 'Captain', permissions: ['Limited roster', 'Team communication', 'Player coordination'] },
            { role: 'Player', permissions: ['Game participation', 'View schedules', 'Team activities'] }
          ]
        },
        {
          id: 'team-5',
          question: 'How do I manage my team roster?',
          answer: 'Team Owners and Coaches can manage rosters through the Team Management section. Add/remove players, update player status, assign roles, and track player availability for games and events.',
          actionButtons: [
            { label: 'My Teams', action: () => navigate('/teams'), variant: 'default' as const, icon: <Users className="h-4 w-4" /> }
          ]
        },
        {
          id: 'team-5b',
          question: 'How do I manage player registrations?',
          answer: 'Coaches and Team Owners can view and manage player registration requests through the Player Registration interface. This includes reviewing applications, updating registration status, managing team assignments, and tracking player availability.',
          actionButtons: [
            { label: 'View Registrations', action: openRegistrationModal, variant: 'default' as const, icon: <UserCheck className="h-4 w-4" /> }
          ],
          registrationFeatures: [
            'Review registration requests',
            'Update registration status',
            'Manage team assignments',
            'Track player availability',
            'Add status notes',
            'Multi-team management'
          ]
        },
        {
          id: 'team-6',
          question: 'Can I be on multiple teams?',
          answer: 'Yes, players can join multiple teams as long as there are no scheduling conflicts. You\'ll need to manage your availability carefully to avoid overlapping commitments.',
          actionButtons: [
            { label: 'My Teams', action: () => navigate('/teams'), variant: 'default' as const, icon: <Users className="h-4 w-4" /> }
          ]
        }
      ]
    },
    {
      category: 'Referral System',
      icon: <Target className="h-5 w-5" />,
      questions: [
        {
          question: 'How does the referral program work?',
          answer: 'Share your unique referral code with friends and family. When they sign up and complete their profile verification, both you and the new user receive bonus points and special rewards.'
        },
        {
          question: 'What rewards do I get for referrals?',
          answer: 'Referral rewards include bonus points, platform credits, exclusive badges, and access to special features. Rewards increase with the number of successful referrals you make.'
        },
        {
          question: 'How do I track my referral success?',
          answer: 'View your referral dashboard to see pending invitations, successful sign-ups, earned rewards, and your position on the referral leaderboard. Track progress toward referral milestones.'
        },
        {
          question: 'Are there referral leaderboards?',
          answer: 'Yes, monthly and all-time referral leaderboards showcase top referrers. Leaderboard positions come with additional rewards, recognition, and sometimes exclusive platform privileges.'
        },
        {
          question: 'Do referral rewards expire?',
          answer: 'Referral rewards typically don\'t expire, but specific promotional bonuses may have time limits. Check the terms of each referral campaign for specific expiration details.'
        }
      ]
    },
    {
      category: 'Support & Help',
      icon: <HelpCircle className="h-5 w-5" />,
      description: 'Get help when you need it with multiple support options.',
      quickStats: '24-hour response time',
      questions: [
        {
          id: 'support-1',
          question: 'How do I submit a support ticket?',
          answer: 'Navigate to the Support section in your account settings or use the "Contact Support" option in the help menu. Provide detailed information about your issue including screenshots if applicable.',
          actionButtons: [
            { label: 'Submit Ticket', action: () => navigate('/support/ticket'), variant: 'default' as const, icon: <HeadphonesIcon className="h-4 w-4" /> },
            { label: 'Live Chat', action: () => navigate('/support/chat'), variant: 'outline' as const, icon: <MessageCircle className="h-4 w-4" /> }
          ],
          contactMethods: [
            { method: 'Support Ticket', response: '24 hours', description: 'Best for detailed issues', icon: <FileText className="h-4 w-4" /> },
            { method: 'Live Chat', response: 'Real-time', description: 'Quick questions and guidance', icon: <MessageCircle className="h-4 w-4" /> },
            { method: 'Phone Support', response: '1-2 hours', description: 'Urgent issues only', icon: <Phone className="h-4 w-4" /> },
            { method: 'Email', response: '24-48 hours', description: 'Non-urgent inquiries', icon: <Mail className="h-4 w-4" /> }
          ]
        },
        {
          id: 'support-2',
          question: 'What should I include in support requests?',
          answer: 'Include your account information, detailed description of the issue, steps to reproduce the problem, any error messages, and screenshots or videos if relevant. The more detail, the faster we can help.',
          actionButtons: [
            { label: 'Ticket Template', action: () => navigate('/support/template'), variant: 'outline' as const, icon: <FileText className="h-4 w-4" /> }
          ],
          ticketChecklist: [
            'Account email and username',
            'Detailed problem description',
            'Steps to reproduce the issue',
            'Error messages (copy exact text)',
            'Screenshots or screen recordings',
            'Device and browser information',
            'When the issue first occurred'
          ]
        },
        {
          id: 'support-3',
          question: 'What are your support response times?',
          answer: 'We aim to respond to all support requests within 24 hours during business days. Urgent issues related to safety or game disruptions receive priority handling and faster response times.',
          supportTiers: [
            { priority: 'Critical', response: '1-2 hours', description: 'Safety issues, game disruptions' },
            { priority: 'High', response: '4-8 hours', description: 'Account access, payment issues' },
            { priority: 'Medium', response: '24 hours', description: 'Feature questions, general help' },
            { priority: 'Low', response: '48 hours', description: 'Enhancement requests, feedback' }
          ]
        },
        {
          id: 'support-4',
          question: 'Can I get help through the app?',
          answer: 'Yes, the mobile app includes a comprehensive help section, FAQ access, and direct support ticket submission. Most common issues can be resolved through the in-app help resources.',
          actionButtons: [
            { label: 'Download App', action: () => window.open('/download', '_blank'), variant: 'default' as const, icon: <Smartphone className="h-4 w-4" /> }
          ]
        },
        {
          id: 'support-5',
          question: 'What self-service options are available?',
          answer: 'Our FAQ covers most common questions, the help section includes step-by-step guides, and video tutorials are available for complex features. Try these resources first for immediate assistance.',
          actionButtons: [
            { label: 'Help Center', action: () => navigate('/help'), variant: 'default' as const, icon: <BookOpen className="h-4 w-4" /> },
            { label: 'Video Tutorials', action: () => {}, variant: 'outline' as const, icon: <PlayCircle className="h-4 w-4" /> }
          ],
          selfServiceOptions: [
            'Comprehensive FAQ',
            'Step-by-step guides',
            'Video tutorials',
            'Community forums',
            'In-app help tooltips'
          ]
        }
      ]
    },
    {
      category: 'Account & Profile Management',
      icon: <Settings className="h-5 w-5" />,
      questions: [
        {
          question: 'How do I update my profile information?',
          answer: 'Access your profile settings through the user menu. Update personal information, contact details, emergency contacts, and role-specific information. Some changes may require re-verification.'
        },
        {
          question: 'Can I change my account role?',
          answer: 'Role changes are possible but may require verification and approval. Contact support with justification for the role change. Some roles have specific requirements that must be met.'
        },
        {
          question: 'How do I manage my privacy settings?',
          answer: 'Navigate to Privacy Settings in your account menu. Control profile visibility, notification preferences, data sharing options, and communication settings. You have full control over your privacy.'
        },
        {
          question: 'What security features are available?',
          answer: 'Enable two-factor authentication, regularly update your password, review login activity, and manage device access. Report any suspicious activity immediately through our security center.'
        },
        {
          question: 'How do I delete my account?',
          answer: 'Account deletion is available through Privacy Settings. This action is permanent and will remove all your data. Download any important information before proceeding with deletion.'
        }
      ]
    },
    {
      category: 'Privacy & Security',
      icon: <Shield className="h-5 w-5" />,
      questions: [
        {
          question: 'Is my personal information safe?',
          answer: 'Yes, we take privacy seriously. Your personal information is encrypted and stored securely. We never share your data with third parties without your consent.'
        },
        {
          question: 'Can I control who sees my profile?',
          answer: 'Yes, you have full control over your privacy settings. You can choose what information is visible to other users and manage your visibility preferences.'
        },
        {
          question: 'How do I block or report inappropriate users?',
          answer: 'Use the block and report features available on user profiles and in messages. Blocked users cannot contact you or see your activity. Reports are reviewed by our moderation team promptly.'
        },
        {
          question: 'What data do you collect and why?',
          answer: 'We collect only necessary data for platform functionality including profile information, game participation, and usage analytics. All data collection is transparent and explained in our privacy policy.'
        },
        {
          question: 'How do I control my notification preferences?',
          answer: 'Manage notifications through your account settings. Choose which events trigger notifications, preferred delivery methods (email, push, in-app), and notification frequency.'
        }
      ]
    },
    {
      category: 'Mobile App Features',
      icon: <Smartphone className="h-5 w-5" />,
      questions: [
        {
          question: 'What features are available on mobile?',
          answer: 'The mobile app includes all core features: game management, team communication, scorekeeping tools, real-time notifications, and offline access to key information.'
        },
        {
          question: 'How do I manage notifications on mobile?',
          answer: 'Access notification settings through the mobile app menu. Customize push notifications, sounds, and vibration patterns. Set quiet hours and priority notification types.'
        },
        {
          question: 'Can I use the app offline?',
          answer: 'Limited offline functionality is available including viewing cached game information, accessing emergency contacts, and basic profile data. Full sync occurs when connection is restored.'
        },
        {
          question: 'How do I navigate efficiently on mobile?',
          answer: 'Use the bottom navigation bar for quick access to main sections. Swipe gestures are supported in many areas. Use the search function to quickly find specific teams, games, or features.'
        },
        {
          question: 'Are mobile notifications reliable?',
          answer: 'Yes, we use multiple delivery methods to ensure important notifications reach you. Critical game-related notifications have backup delivery methods for maximum reliability.'
        }
      ]
    },
    {
      category: 'Advanced Game Features',
      icon: <Zap className="h-5 w-5" />,
      questions: [
        {
          question: 'How does game workflow automation work?',
          answer: 'Our platform automatically manages game workflows including pre-game reminders, confirmation tracking, roster validation, and post-game reporting. Reduces manual coordination effort.'
        },
        {
          question: 'What statistics are tracked during games?',
          answer: 'We track comprehensive game statistics including player performance, team metrics, scorekeeping accuracy, and engagement levels. Data is used for analytics and improvement insights.'
        },
        {
          question: 'How do performance analytics work?',
          answer: 'Performance analytics provide insights into player, team, and organizational performance. View trends, comparisons, and recommendations based on historical data and benchmarks.'
        },
        {
          question: 'Can I export game data and statistics?',
          answer: 'Yes, game data and statistics can be exported in various formats including CSV, PDF reports, and integration with external analytics tools. Data export options are available in your dashboard.'
        },
        {
          question: 'How does real-time game tracking work?',
          answer: 'Real-time tracking provides live updates on game progress, scores, player substitutions, and key events. All stakeholders receive instant notifications about game developments.'
        }
      ]
    },
    {
      category: 'Parent & Child Accounts',
      icon: <Users className="h-5 w-5" />,
      description: 'Managing accounts for children under 13, spending limits, and parental controls.',
      quickStats: '$50 child spending limit',
      questions: [
        {
          id: 'parent-child-1',
          question: 'Why does my child have a $50 spending limit?',
          answer: 'To protect families, children under 13 have a $50 lifetime spending limit on our platform. This aligns with federal guidelines that limit parental liability for unauthorized purchases made by minors to $50. Once your child reaches this limit, you can gift them points through your Parent Dashboard.',
          highlightBox: {
            type: 'info',
            content: '💡 The $50 limit protects your family by ensuring you maintain control over all spending. Parents can gift unlimited points to their children.'
          },
          actionButtons: [
            { label: 'Go to Parent Dashboard', action: () => navigate('/dashboard'), variant: 'default' as const, icon: <Users className="h-4 w-4" /> },
            { label: 'Buy Points to Gift', action: () => navigate('/points/buy'), variant: 'outline' as const, icon: <Gift className="h-4 w-4" /> }
          ]
        },
        {
          id: 'parent-child-2',
          question: 'How can I give my child more points after they reach the limit?',
          answer: 'Visit the "Buy Points" page to purchase points, then use the "Gift Points" feature in your Parent Dashboard to send points to your connected child. This ensures you maintain full control over spending while your child can still enjoy premium features.',
          actionButtons: [
            { label: 'Buy Points', action: () => navigate('/points/buy'), variant: 'default' as const, icon: <Gift className="h-4 w-4" /> },
            { label: 'Parent Dashboard', action: () => navigate('/dashboard'), variant: 'outline' as const, icon: <Users className="h-4 w-4" /> }
          ],
          steps: [
            { step: 1, title: 'Purchase Points', description: 'Buy a points package from the Buy Points page' },
            { step: 2, title: 'Open Parent Dashboard', description: 'Navigate to your dashboard' },
            { step: 3, title: 'Select Child', description: 'Choose the child you want to gift points to' },
            { step: 4, title: 'Gift Points', description: 'Enter amount and send points to your child' }
          ]
        },
        {
          id: 'parent-child-3',
          question: 'Can I increase my child\'s spending limit?',
          answer: 'The $50 limit is set to protect families and cannot be increased. This ensures that children cannot accidentally make large purchases without parental oversight. However, there is no limit on how many points you can gift to your child - you can purchase and gift as many points as you wish.',
          highlightBox: {
            type: 'warning',
            content: '⚠️ The spending limit cannot be modified to ensure maximum family protection from unauthorized charges.'
          }
        },
        {
          id: 'parent-child-4',
          question: 'What happens when my child tries to buy something over the limit?',
          answer: 'Your child will see a friendly message explaining they\'ve reached their spending limit and suggesting they ask you to gift them points instead. The purchase button will be disabled for any packages that would exceed their remaining limit.',
          actionButtons: [
            { label: 'Connect with Your Child', action: () => navigate('/parent/connect'), variant: 'outline' as const, icon: <UserPlus className="h-4 w-4" /> }
          ]
        },
        {
          id: 'parent-child-5',
          question: 'Is the $50 limit per month or lifetime?',
          answer: 'The $50 limit applies to your child\'s lifetime account spending, not monthly. This aligns with federal consumer protection guidelines regarding minors\' purchasing capability. Once reached, only gifted points can add to their balance.',
        },
        {
          id: 'parent-child-6',
          question: 'How do I connect my account to my child\'s account?',
          answer: 'Use the Parent-Child Connection feature in your account settings. Your child can generate a connection code from their account, which you enter in your Parent Dashboard to establish the link. This allows you to monitor activity and gift points.',
          actionButtons: [
            { label: 'Connect to Child', action: () => navigate('/parent/connect'), variant: 'default' as const, icon: <UserPlus className="h-4 w-4" /> }
          ]
        },
        {
          id: 'parent-child-7',
          question: 'Can I see my child\'s spending history?',
          answer: 'Yes! Connected parents can view their child\'s purchase history, current spending against the $50 limit, and point balance through the Parent Dashboard. This gives you full visibility into your child\'s account activity.',
          actionButtons: [
            { label: 'View in Dashboard', action: () => navigate('/dashboard'), variant: 'default' as const, icon: <Users className="h-4 w-4" /> }
          ]
        }
      ]
    },
    {
      category: 'Founding Agent Beta Program',
      icon: <Award className="h-5 w-5" />,
      description: 'Information about the Agent / NIL Tools Beta program for licensed professionals.',
      quickStats: '200 spots available',
      questions: [
        {
          id: 'founding-agent-1',
          question: 'What is the Founding Agent Beta Program?',
          answer: 'The Founding Agent Beta Program is an exclusive opportunity for the first 200 approved agent and NIL professionals to access our Agent / NIL Tools at special founding rates. Founding Agents receive 24 months of access for the price of 12, lifetime price lock, and priority feature access.',
          actionButtons: [
            { label: 'Apply Now', action: () => navigate('/agent-beta'), variant: 'default' as const, icon: <Award className="h-4 w-4" /> },
            { label: 'View Pricing', action: () => navigate('/agent/pricing'), variant: 'outline' as const, icon: <FileText className="h-4 w-4" /> }
          ]
        },
        {
          id: 'founding-agent-2',
          question: 'How long do I have to complete payment after approval?',
          answer: 'Approved applicants have 7 days to complete their subscription payment. If payment is not completed within this window, your Founding Agent spot may be offered to the next applicant in line. This ensures we can reach our goal of 200 Founding Agents efficiently.',
          actionButtons: [
            { label: 'Check Status', action: () => navigate('/agent/application-status'), variant: 'default' as const, icon: <Clock className="h-4 w-4" /> }
          ]
        },
        {
          id: 'founding-agent-3',
          question: 'What happens if I miss the 7-day payment window?',
          answer: 'If you miss the 7-day payment window, your approved status will be revoked and your spot will be offered to the next applicant. You would need to reapply and go through the approval process again, subject to spot availability.',
        },
        {
          id: 'founding-agent-4',
          question: 'What pricing do Founding Agents receive?',
          answer: 'Founding Agents receive exclusive BETA pricing: pay for 12 months and get 24 months of access. This represents a 50% discount compared to standard annual pricing. Additionally, your rate is locked for life - you will never pay more than your founding rate.',
        },
        {
          id: 'founding-agent-5',
          question: 'Do I need to pay before applying?',
          answer: 'No, the application is completely free. Payment is only required after your application has been reviewed and approved. The 2-3 business day review process allows us to verify applicant credentials before granting access.',
        },
        {
          id: 'founding-agent-6',
          question: 'What does "lifetime price lock" mean?',
          answer: 'Lifetime price lock means the subscription rate you lock in as a Founding Agent will never increase, even if we raise prices for new subscribers in the future. As long as you maintain an active subscription, your founding rate remains guaranteed.',
        },
        {
          id: 'founding-agent-7',
          question: 'Is Players Lockeroom acting as my representation?',
          answer: 'No. Players Lockeroom provides tools for NIL-related workflows; it is not acting as representation or agent. We do not negotiate deals, solicit agreements, receive compensation tied to NIL earnings, or provide legal, financial, or contract advice.',
        }
      ]
    }
  ];

  // Calculate overall stats
  const totalQuestions = faqCategories.reduce((sum, category) => sum + category.questions.length, 0);

  const filteredCategories = faqCategories.filter(category => {
    // Apply search filter
    const matchesSearch = category.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      category.questions.some(q =>
        q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchQuery.toLowerCase())
      );
    
    // Apply category filter
    const matchesCategory = !selectedCategoryFilter || category.category === selectedCategoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  const scrollToCategory = (category: string) => {
    const element = categoryRefs.current[category];
    if (element) {
      const offset = 120; // Account for sticky header
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
      setActiveCategory(category);
    }
  };

  const handleCategoryFilter = (category: string) => {
    setSelectedCategoryFilter(category);
    // Scroll to top when filtering
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const clearCategoryFilter = () => {
    setSelectedCategoryFilter(null);
  };

  // Track scroll position for active category highlighting
  useEffect(() => {
    const handleScroll = () => {
      if (selectedCategoryFilter) return; // Don't update active when filtering
      
      const scrollPosition = window.scrollY + 150;
      
      for (const category of faqCategories) {
        const element = categoryRefs.current[category.category];
        if (element) {
          const elementTop = element.offsetTop;
          const elementBottom = elementTop + element.offsetHeight;
          
          if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
            setActiveCategory(category.category);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [selectedCategoryFilter]);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title="FAQ" description="Frequently asked questions about Players Lockeroom — accounts, teams, games, and more." path="/faq" />
      {/* Headers */}
      {isMobile ? <MobileHeader /> : <Header />}
      
      <div className={`${isMobile ? 'pt-16 pb-20' : 'pt-20 pb-8'}`}>
        <div className="container mx-auto px-4">
          <Card className="bg-card/50 backdrop-blur border-border">
            <CardHeader className="text-center">
              <CardTitle className="text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                Frequently Asked Questions
              </CardTitle>
              <CardDescription className="text-center max-w-2xl mx-auto">
                Find answers to common questions about SportManager. Use the search bar or browse by category.
              </CardDescription>
              
              {/* Quick Actions */}
              <div className="flex flex-wrap justify-center gap-3 mt-6">
                {quickActions.map((action, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={action.action}
                    className="flex items-center gap-2"
                  >
                    <div className={`w-2 h-2 rounded-full ${action.color}`}></div>
                    {action.icon}
                    {action.label}
                  </Button>
                ))}
              </div>
              
              <div className="flex justify-center gap-4 mt-4 text-sm text-muted-foreground">
                <span>{faqCategories.length} categories</span>
                <span>•</span>
                <span>{totalQuestions} questions</span>
              </div>
            </CardHeader>
            <CardContent>
              {/* Search Section */}
              <div className="relative mb-6">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search frequently asked questions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Category Navigation */}
              {!searchQuery && (
                <div className="px-4 py-3 bg-background/95 backdrop-blur sticky top-28 z-40 border-b mb-6 rounded-lg">
                  <ScrollArea className="w-full">
                    {/* Show "All Categories" button when a filter is active */}
                    {selectedCategoryFilter && (
                      <div className="mb-4">
                        <Button
                          variant="default"
                          onClick={clearCategoryFilter}
                          className="flex items-center gap-2"
                        >
                          <Grid3X3 className="h-4 w-4" />
                          All Categories
                        </Button>
                      </div>
                    )}
                    
                    {/* Mobile Compact Grid Layout */}
                    <div className="block md:hidden">
                      <div className="grid grid-cols-2 gap-2 pb-2 overflow-x-auto">
                        {faqCategories.map((category) => (
                          <Button
                            key={category.category}
                            variant={selectedCategoryFilter === category.category ? "default" : "outline"}
                            onClick={() => handleCategoryFilter(category.category)}
                            className="h-14 p-2 flex flex-col items-center justify-center gap-1 text-center text-xs whitespace-normal"
                          >
                            {React.cloneElement(category.icon, { className: "h-3 w-3 flex-shrink-0" })}
                            <span className="leading-tight font-medium text-[10px]">{category.category}</span>
                          </Button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Desktop Horizontal Layout */}
                    <div className="hidden md:block">
                      <ScrollArea className="w-full">
                        <div className="flex space-x-2 pb-2 overflow-x-auto">
                          {faqCategories.map((category) => (
                            <Button
                              key={category.category}
                              variant={selectedCategoryFilter === category.category ? "default" : "outline"}
                              onClick={() => handleCategoryFilter(category.category)}
                              className="whitespace-nowrap flex items-center gap-2 flex-shrink-0"
                            >
                              {category.icon}
                              {category.category}
                            </Button>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* FAQ Content */}
              <div className="space-y-8">
                {filteredCategories.length === 0 ? (
                  <Card className="bg-muted/50">
                    <CardContent className="flex flex-col items-center justify-center py-12">
                      <Search className="h-12 w-12 text-muted-foreground mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No results found</h3>
                      <p className="text-muted-foreground text-center">
                        Try adjusting your search terms or browse our categories above
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  filteredCategories.map((category) => (
                    <div
                      key={category.category}
                      ref={(el) => categoryRefs.current[category.category] = el}
                      className="space-y-4"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          {category.icon}
                        </div>
                        <h2 className="text-2xl font-bold text-foreground">
                          {category.category}
                        </h2>
                        <Badge variant="secondary" className="ml-auto">
                          {category.questions.length} questions
                        </Badge>
                      </div>
                      
                      <Accordion type="single" collapsible className="w-full" value={openAccordionItem} onValueChange={setOpenAccordionItem}>
                        {category.questions.map((faq, index) => (
                          <AccordionItem
                            key={index}
                            value={`${category.category}-${index}`}
                            id={`faq-${category.category.toLowerCase().replace(/\s+/g, '-')}-${index}`}
                            className="border border-border rounded-lg mb-4 overflow-hidden"
                          >
                            <AccordionTrigger className="text-left hover:no-underline px-6 py-4 hover:bg-muted/50 transition-colors">
                              <div className="flex items-center space-x-3">
                                <div className="p-1.5 bg-primary/10 rounded-lg">
                                  <HelpCircle className="h-4 w-4 text-primary" />
                                </div>
                                <span className="font-medium">{faq.question}</span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-6 pb-6 pt-2">
                              <div className="space-y-4">
                                <p className="text-muted-foreground leading-relaxed">
                                  {faq.answer}
                                </p>

                                {/* Action Buttons */}
                                {faq.actionButtons && (
                                  <div className="flex flex-wrap gap-2">
                                    {faq.actionButtons
                                      .filter((button: any) => !button.adminOnly || isAdmin)
                                      .map((button, btnIndex) => (
                                      <Button
                                        key={btnIndex}
                                        variant={button.variant}
                                        size="sm"
                                        onClick={button.action}
                                        className="flex items-center gap-2"
                                      >
                                        {button.icon}
                                        {button.label}
                                      </Button>
                                    ))}
                                  </div>
                                )}

                                {/* Role Cards - Admin Only */}
                                {faq.roleCards && isAdmin && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                                    {faq.roleCards.map((role, roleIndex) => (
                                      <Card key={roleIndex} className="p-4 bg-muted/30">
                                        <h4 className="font-semibold text-sm mb-2">{role.role}</h4>
                                        <p className="text-xs text-muted-foreground mb-3">{role.description}</p>
                                        <ul className="text-xs space-y-1">
                                          {role.permissions.map((permission, permIndex) => (
                                            <li key={permIndex} className="flex items-center gap-1">
                                              <CheckCircle2 className="h-3 w-3 text-green-500" />
                                              {permission}
                                            </li>
                                          ))}
                                        </ul>
                                      </Card>
                                    ))}
                                  </div>
                                )}

                                {/* Steps */}
                                {faq.steps && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                                    {faq.steps.map((step, stepIndex) => (
                                      <div key={stepIndex} className="text-center p-3 rounded-lg bg-muted/30">
                                        <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-2 text-sm font-bold">
                                          {step.step}
                                        </div>
                                        <h4 className="font-semibold text-sm mb-1">{step.title}</h4>
                                        <p className="text-xs text-muted-foreground">{step.description}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Checklist */}
                                {faq.checklist && (
                                  <div className="bg-muted/30 rounded-lg p-4">
                                    <h4 className="font-semibold text-sm mb-3">Getting Started Checklist</h4>
                                    <div className="space-y-2">
                                      {faq.checklist.map((item, itemIndex) => (
                                        <div key={itemIndex} className="flex items-center gap-2">
                                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                                          <span className="text-sm">{item}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Timeline */}
                                {faq.timeline && (
                                  <div className="bg-muted/30 rounded-lg p-4">
                                    <h4 className="font-semibold text-sm mb-3">Game Confirmation Timeline</h4>
                                    <div className="space-y-3">
                                      {faq.timeline.map((timelineItem, timeIndex) => (
                                        <div key={timeIndex} className="flex items-start gap-3">
                                          <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                                          <div>
                                            <div className="font-medium text-sm">{timelineItem.time}</div>
                                            <div className="text-xs text-muted-foreground">{timelineItem.description}</div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Code Example */}
                                {faq.codeExample && (
                                  <div className="bg-muted/30 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-2">
                                      <h4 className="font-semibold text-sm">Example Code</h4>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => copyToClipboard(faq.codeExample!, `code-${faq.id}`)}
                                        className="h-6 px-2"
                                      >
                                        {copiedCode === `code-${faq.id}` ? (
                                          <CheckCircle2 className="h-3 w-3 text-green-500" />
                                        ) : (
                                          <Copy className="h-3 w-3" />
                                        )}
                                      </Button>
                                    </div>
                                    <code className="text-sm font-mono bg-background px-2 py-1 rounded">
                                      {faq.codeExample}
                                    </code>
                                  </div>
                                )}

                                {/* Points Table */}
                                {faq.pointsTable && (
                                  <div className="bg-muted/30 rounded-lg p-4">
                                    <h4 className="font-semibold text-sm mb-3">Point System</h4>
                                    <div className="space-y-2">
                                      {faq.pointsTable.map((row, rowIndex) => (
                                        <div key={rowIndex} className="flex justify-between items-center text-sm">
                                          <span>{row.gameType}</span>
                                          <div className="text-right">
                                            <div className="font-medium">{row.points}</div>
                                            <div className="text-xs text-muted-foreground">{row.bonus}</div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Join Methods */}
                                {faq.joinMethods && (
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                                    {faq.joinMethods.map((method, methodIndex) => (
                                      <div key={methodIndex} className="text-center p-3 rounded-lg bg-muted/30">
                                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
                                          {method.icon}
                                        </div>
                                        <h4 className="font-semibold text-sm mb-1">{method.method}</h4>
                                        <p className="text-xs text-muted-foreground">{method.description}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Contact Methods */}
                                {faq.contactMethods && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                                    {faq.contactMethods.map((contact, contactIndex) => (
                                      <div key={contactIndex} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                                          {contact.icon}
                                        </div>
                                        <div className="flex-1">
                                          <div className="font-semibold text-sm">{contact.method}</div>
                                          <div className="text-xs text-muted-foreground">{contact.description}</div>
                                        </div>
                                        <Badge variant="outline" className="text-xs">
                                          {contact.response}
                                        </Badge>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Requirements */}
                                {faq.requirements && (
                                  <div className="bg-muted/30 rounded-lg p-4">
                                    <h4 className="font-semibold text-sm mb-3">Requirements</h4>
                                    <div className="space-y-2">
                                      {faq.requirements.map((req, reqIndex) => (
                                        <div key={reqIndex} className="flex items-center gap-2">
                                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                                          <span className="text-sm">{req}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Responsibilities */}
                                {faq.responsibilities && (
                                  <div className="bg-muted/30 rounded-lg p-4">
                                    <h4 className="font-semibold text-sm mb-3">Key Responsibilities</h4>
                                    <div className="space-y-2">
                                      {faq.responsibilities.map((resp, respIndex) => (
                                        <div key={respIndex} className="flex items-center gap-2">
                                          <CheckCircle2 className="h-4 w-4 text-blue-500" />
                                          <span className="text-sm">{resp}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Ticket Checklist */}
                                {faq.ticketChecklist && (
                                  <div className="bg-muted/30 rounded-lg p-4">
                                    <h4 className="font-semibold text-sm mb-3">Include in Your Ticket</h4>
                                    <div className="space-y-2">
                                      {faq.ticketChecklist.map((item, itemIndex) => (
                                        <div key={itemIndex} className="flex items-center gap-2">
                                          <CheckCircle2 className="h-4 w-4 text-orange-500" />
                                          <span className="text-sm">{item}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Support Tiers */}
                                {faq.supportTiers && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                                    {faq.supportTiers.map((tier, tierIndex) => (
                                      <div key={tierIndex} className="p-3 rounded-lg bg-muted/30 border-l-4 border-primary">
                                        <div className="flex justify-between items-center mb-1">
                                          <span className="font-semibold text-sm">{tier.priority}</span>
                                          <Badge variant="outline" className="text-xs">{tier.response}</Badge>
                                        </div>
                                        <p className="text-xs text-muted-foreground">{tier.description}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Self Service Options */}
                                {faq.selfServiceOptions && (
                                  <div className="bg-muted/30 rounded-lg p-4">
                                    <h4 className="font-semibold text-sm mb-3">Self-Service Options</h4>
                                    <div className="space-y-2">
                                      {faq.selfServiceOptions.map((option, optionIndex) => (
                                        <div key={optionIndex} className="flex items-center gap-2">
                                          <CheckCircle2 className="h-4 w-4 text-purple-500" />
                                          <span className="text-sm">{option}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Was this helpful? */}
                                <div className="flex items-center justify-between pt-4 border-t">
                                  <span className="text-sm text-muted-foreground">Was this helpful?</span>
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      variant={helpfulVotes[faq.id] === 'up' ? 'default' : 'ghost'}
                                      onClick={() => handleHelpfulVote(faq.id, 'up')}
                                      className="h-8 px-3"
                                    >
                                      <ThumbsUp className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant={helpfulVotes[faq.id] === 'down' ? 'default' : 'ghost'}
                                      onClick={() => handleHelpfulVote(faq.id, 'down')}
                                      className="h-8 px-3"
                                    >
                                      <ThumbsDown className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Desktop Footer */}
      {!isMobile && <Footer />}

      {/* Mobile Bottom Navigation */}
      {isMobile && <MobileBottomNav />}
      
      {/* Player Registration Modal */}
      <PlayerRegistrationModalComponent />
    </div>
  );
};

export default FAQ;